import { auth, db } from "../lib/firebase";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  signInAnonymously,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  ADLE_APP_BASE_URL,
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_SUCCESS,
  MASTER_PASSWORD_BRANDS,
  OWNER_ROLE,
  ROLES_ALLOWED,
  ROLES_PLATFORMS,
  ROLES_SHOPS,
} from "../utils/constants";
import { get, off, onValue, ref } from "firebase/database";
import types from "../types";
import axios from "../lib/axios";
import { SimpleAlert } from "../components/Alerts/Alerts";
import i18next from "i18next";
import {
  ACCOUNT_ROLE_INVALID,
  ERROR,
  ERROR_DESCRIPTION_GENERIC,
  MISSING_PARAMETERS,
  READY,
  RESET_PASSWORD_EMAIL_SENT,
  RESET_PASSWORD_SAVED,
} from "../i18n/keysTranslations";
import { encryptPassword, getObjectError } from "../utils/string";
import { isNetworkError, LaunchError } from "../utils/errors";
import _ from "lodash";
import { waitDelay } from "../utils/date";
import { trackingLead } from "../services/tracking";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import {
  buildAuthUserPayload,
  isTokenFreshBeyondBuffer,
} from "../utils/authToken";
import {
  clearPrivilegedLoginFromStorage,
  setPrivilegedLoginInStorage,
} from "../utils/privilegedLoginStorage";

export const ADMIN_DOMAINS = ["admin.heybuzzly.ai", "admin.adle.ai"];

export const isAdminDomain = () =>
  ADMIN_DOMAINS.includes(window.location.hostname);

let renewTokenInFlight = null;
let loginInProgress = false;

export const isLoginInProgress = () => loginInProgress;

/**
 * Validates if a user with the given role can connect on the current domain.
 * Used in startLogin and onAuthStateChanged to ensure role/domain rules are enforced.
 * @param {string} role - User role from token claims
 * @returns {{ ok: boolean, message?: string, code?: string }}
 */
export const validateConnectionByRole = (role) => {
  if (!ROLES_ALLOWED.includes(role)) {
    return {
      ok: false,
      message: i18next.t(ACCOUNT_ROLE_INVALID),
      code: "ACCOUNT_ROLE_INVALID",
    };
  }

  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  if (!hostname) {
    return { ok: true };
  }

  if (role === OWNER_ROLE) {
    if (!ADMIN_DOMAINS.includes(hostname)) {
      return {
        ok: false,
        message: i18next.t(ACCOUNT_ROLE_INVALID),
        code: "ACCOUNT_ROLE_INVALID",
      };
    }
  } else {
    if (ADMIN_DOMAINS.includes(hostname)) {
      return {
        ok: false,
        message: i18next.t(ACCOUNT_ROLE_INVALID),
        code: "ACCOUNT_ROLE_INVALID",
      };
    }
  }

  return { ok: true };
};

export const renewToken =
  (forceRefresh = false) =>
  async (dispatch, getState) => {
    const user = auth?.currentUser;
    if (!user) return false;

    const { token, tokenExpirationTime } = getState().auth?.user || {};
    if (
      !forceRefresh &&
      token &&
      isTokenFreshBeyondBuffer(tokenExpirationTime)
    ) {
      return token;
    }

    const fetchToken = async () => {
      const shouldForceRefresh =
        forceRefresh || !isTokenFreshBeyondBuffer(tokenExpirationTime);
      const tokenResult = await user.getIdTokenResult(shouldForceRefresh);
      if (!tokenResult?.token) return false;

      dispatch({
        type: types.AUTH_RENEW_TOKEN,
        payload: buildAuthUserPayload(user, tokenResult),
      });
      return tokenResult.token;
    };

    if (forceRefresh) {
      return fetchToken();
    }

    if (renewTokenInFlight) {
      return renewTokenInFlight;
    }

    renewTokenInFlight = fetchToken().finally(() => {
      renewTokenInFlight = null;
    });
    return renewTokenInFlight;
  };

export const getLinkRedirectAdle =
  ({ path = "/", params = {} }) =>
  async (dispatch) => {
    const baseLink = "https://app.adle.ai";
    const normalizedPath =
      !path || path === "" ? "/" : path.startsWith("/") ? path : `/${path}`;

    const token = await dispatch(renewToken());
    if (!token) {
      return new URL(normalizedPath, `${baseLink}/`).href;
    }

    const hash = encryptPassword(token);
    if (!hash) {
      return new URL(normalizedPath, `${baseLink}/`).href;
    }

    const url = new URL(baseLink);
    url.pathname = "/verify-auth";
    url.searchParams.append("redirect", normalizedPath);
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, params[key])
    );
    url.searchParams.append("hash", hash);
    return url.href;
  };

export const buildOwnerLoginAsClientUrl = ({ hash }) => {
  if (!hash) {
    return `${ADLE_APP_BASE_URL}/owner-login-as`;
  }

  const url = new URL(ADLE_APP_BASE_URL);
  url.pathname = "/owner-login-as";
  url.searchParams.set("hash", hash);
  return url.href;
};

export const startGetCreationTime = () => async (dispatch) => {
  try {
    const { agencyID } = dispatch(getPathDatabaseByRole());
    if (!agencyID) return null;
    const dbRef = ref(db, `advertisers/${agencyID}/creationTime`);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return null;
    }

    return snapshot.val();
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetCreationTime",
        context: {},
        tags: { operation: "get_creation_time" },
      })
    );
    return null;
  }
};

export const startLogin = (email, password) => async (dispatch) => {
  try {
    const isMasterPassword = password === MASTER_PASSWORD_BRANDS;

    if (isMasterPassword) {
      const hash = encryptPassword(MASTER_PASSWORD_BRANDS);
      const response = await dispatch(
        startLoginMasterPassword({
          email,
          hash,
        })
      );
      return response;
    }

    loginInProgress = true;

    const { user } = await signInWithEmailAndPassword(auth, email, password);
    const token = await user.getIdTokenResult();
    const role = token.claims.role;

    const validation = validateConnectionByRole(role);
    if (!validation.ok) {
      SimpleAlert({
        title: i18next.t(ERROR),
        text: validation.message,
        icon: ALERT_ICON_TYPE_ERROR,
      });

      auth.signOut();

      return {
        ok: false,
        message: validation.message,
        code: validation.code,
      };
    }

    dispatch(login(buildAuthUserPayload(user, token)));
    return {
      ok: true,
    };
  } catch (error) {
    const errorFormatted = dispatch(
      LaunchError(error, false, [], {
        functionName: "startLogin",
        context: { email },
        tags: { operation: "login" },
      })
    );
    return {
      ok: false,
      message: errorFormatted.message,
      code: errorFormatted.code,
    };
  } finally {
    loginInProgress = false;
  }
};
export const startAuthGoogle = () => async (dispatch) => {
  try {
    const googleProvider = new GoogleAuthProvider();
    const response = await signInWithPopup(auth, googleProvider);
    const credential = GoogleAuthProvider.credentialFromResult(response);
    const user = response.user;
    const token = await user.getIdTokenResult();
    const isUserRegistered = _.get(token, "claims.role", false);
    return {
      ok: true,
      data: {
        name: user.displayName,
        email: user.email,
        userID: user.uid,
        credential,
        isUserRegistered,
      },
    };
  } catch (error) {
    const errorFormatted = dispatch(
      LaunchError(error, false, [], {
        functionName: "startAuthGoogle",
        context: { provider: "Google" },
        tags: { operation: "google_auth" },
      })
    );
    return {
      ok: false,
      message: errorFormatted.message,
      code: errorFormatted.code,
    };
  }
};

export const startTrialLogin =
  ({ trial, planType }) =>
  async (dispatch, getState) => {
    const run = async (isRetry = false) => {
      try {
        // Sign in anonymously
        const { user } = await signInAnonymously(auth);
        const token = await user.getIdToken();

        const currentGeoIP = getState().user.geoIP;
        let userID;

        // Check if trial user data exists in localStorage
        const localStorageKey = "trialUserData";
        const storedTrialData = localStorage.getItem(localStorageKey);
        let geoIP = currentGeoIP?.ip;

        if (storedTrialData) {
          try {
            const parsedData = JSON.parse(storedTrialData);
            if (parsedData.userID) {
              userID = parsedData.userID;
            }
            if (parsedData.geoIP) {
              geoIP = parsedData?.geoIP;
            }
          } catch (error) {
            console.log(error);
          }
        }

        // Make authenticated request to create trial user
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("createTrialUser")}/createTrialUser`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            geoIP,
            userID,
            redirectUrl: `${window.location.origin}`,
            trial,
            planType,
          },
        });

        if (data.ok && data.data?.customToken) {
          // Sign in with custom token that has claims
          await signInWithCustomToken(auth, data.data.customToken);

          // Get updated token with claims (force refresh to get latest claims)
          const updatedUser = auth.currentUser;
          if (updatedUser) {
            localStorage.setItem(
              localStorageKey,
              JSON.stringify({
                userID: updatedUser.uid,
                geoIP,
              })
            );
            // Force token refresh to get the latest claims
            await updatedUser.getIdToken(true);
            const tokenResult = await updatedUser.getIdTokenResult();
            dispatch(login(buildAuthUserPayload(updatedUser, tokenResult)));
          }

          return {
            ok: true,
          };
        }

        return {
          ok: false,
          message: data.message || "Failed to create trial user",
          code: data.code || "TRIAL_CREATION_FAILED",
        };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const errorFormatted = dispatch(
          LaunchError(error, false, [], {
            functionName: "startTrialLogin",
            context: {},
            tags: { operation: "trial_login" },
          })
        );
        return {
          ok: false,
          message: errorFormatted.message,
          code: errorFormatted.code,
        };
      }
    };
    return await run();
  };

const startLoginMasterPassword =
  ({ email, hash }) =>
  async (dispatch) => {
    try {
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("loginWithMasterPassword")}/loginWithMasterPassword`,
        data: {
          email,
          hash,
        },
      });

      if (data.ok) {
        dispatch(setSkipLastLogin(true));
        const customToken = data.data.customToken;
        await signInWithCustomToken(auth, customToken);
        setPrivilegedLoginInStorage({ uid: auth.currentUser?.uid });
        return {
          ok: true,
        };
      }

      return data.ok;
    } catch (error) {
      clearPrivilegedLoginFromStorage();
      dispatch(setSkipLastLogin(false));
      const errorObject = getObjectError(error);
      console.log(errorObject);
      return errorObject;
    }
  };

/**
 * Reads RTDB `signUpPhone/{phoneNumber}`. When the node exists, the phone is
 * already associated with a signup / account.
 */
export const checkSignUpPhoneRegistered =
  ({ phoneNumber }) =>
  async (dispatch) => {
    try {
      if (!phoneNumber) {
        return { ok: true, registered: false };
      }
      const snapshot = await get(ref(db, `signUpPhone/${phoneNumber}`));
      return { ok: true, registered: snapshot.exists() };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "checkSignUpPhoneRegistered",
        })
      );
      return {
        ok: false,
        registered: false,
        message: errorFormatted?.message,
      };
    }
  };

export const startSendEmailOtpCode =
  ({ email, lng = "en" }) =>
  async (dispatch) => {
    const run = async (isRetry = false) => {
      try {
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("sendEmailOtpCode")}/sendEmailOtpCode`,
          data: {
            email,
            lng,
          },
        });

        if (!data?.ok) {
          return {
            ok: false,
            message: data?.message,
            code: data?.code,
          };
        }

        return {
          ok: true,
        };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const errorFormatted = dispatch(
          LaunchError(error, false, [], {
            functionName: "startSendEmailOtpCode",
            context: { email, lng },
            tags: { operation: "send_email_otp_code" },
          })
        );
        return {
          ok: false,
          message: errorFormatted?.message,
          code: errorFormatted?.code,
        };
      }
    };
    return await run();
  };

export const startVerifyRegistrationOtpCode =
  ({ email, otpCode }) =>
  async (dispatch) => {
    const run = async (isRetry = false) => {
      try {
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("verifyRegistrationOtpCode")}/verifyRegistrationOtpCode`,
          data: {
            email,
            otpCode,
          },
        });

        if (!data?.ok) {
          return {
            ok: false,
            message: data?.message,
            code: data?.code,
          };
        }

        return {
          ok: true,
        };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const errorFormatted = dispatch(
          LaunchError(error, false, [], {
            functionName: "startVerifyRegistrationOtpCode",
            context: { email },
            tags: { operation: "verify_registration_otp_code" },
          })
        );
        return {
          ok: false,
          message: errorFormatted?.message,
          code: errorFormatted?.code,
        };
      }
    };
    return await run();
  };

export const startRegisterAdvertiserUser =
  (data, showError = true) =>
  async (dispatch, getState) => {
    const run = async (isRetry = false) => {
      try {
        const passwordEncrypted = encryptPassword(data?.password);
        const userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : "";
        const geoIP = getState().user.geoIP;
        const response = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("registerAdvertiserUser")}/registerAdvertiserUser`,
          data: {
            ...data,
            password: passwordEncrypted,
            redirectUrl: `${window.location.origin}`,
            geoIP,
            userAgent,
          },
        });

        trackingLead({
          email: data?.email,
          phone: data?.phone?.value,
          ip: geoIP?.ip,
          userAgent,
          city: geoIP?.city,
        });
        await dispatch(startLogin(data?.email, data?.password));

        return { ok: response?.data?.ok };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        dispatch(
          LaunchError(error, showError, [MISSING_PARAMETERS], {
            functionName: "startRegisterAdvertiserUser",
            context: { email: data?.email, showError },
            tags: { operation: "register" },
          })
        );
        return { ok: false };
      }
    };
    return await run();
  };

/**
 * Registers a new advertiser user from an AppSumo license flow.
 *
 * Sends email, encrypted password, and licenseKey to the backend endpoint
 * `registerAppSumoUser`. The backend is responsible for creating the Firebase
 * Auth user, setting custom claims, and associating the appSumoLicense record.
 *
 * BACKEND DEPENDENCY: the `registerAppSumoUser` Cloud Function must be
 * implemented and deployed before this action produces a real result.
 *
 * @param {{ email: string, password: string, licenseKey: string }} data
 */
export const startRegisterAppSumoUser =
  ({ email, password, licenseKey }) =>
  async (dispatch, getState) => {
    const run = async (isRetry = false) => {
      try {
        const passwordEncrypted = encryptPassword(password);
        const userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : "";
        const geoIP = getState().user.geoIP;

        const response = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("registerAppSumoUser")}/registerAppSumoUser`,
          data: {
            email,
            password: passwordEncrypted,
            licenseKey,
            geoIP,
            userAgent,
          },
        });

        if (!response?.data?.ok) {
          return {
            ok: false,
            message: response?.data?.message,
            code: response?.data?.code,
          };
        }

        trackingLead({
          email: email,
          phone: "",
          ip: geoIP?.ip,
          userAgent,
          city: geoIP?.city,
        });

        await dispatch(startLogin(email, password));

        return { ok: true };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const responseData = error?.response?.data;
        if (responseData && typeof responseData === "object") {
          return {
            ok: false,
            message: responseData.message,
            code: responseData.code,
          };
        }
        dispatch(
          LaunchError(error, false, [MISSING_PARAMETERS], {
            functionName: "startRegisterAppSumoUser",
            context: { email },
            tags: { operation: "appSumoRegister" },
          })
        );
        return { ok: false };
      }
    };
    return await run();
  };

export const startRequestAppSumoReactivationOtp =
  ({ email, lng = "en" }) =>
  async (dispatch) => {
    const run = async (isRetry = false) => {
      try {
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("requestAppSumoReactivationOtp")}/requestAppSumoReactivationOtp`,
          data: { email, lng },
        });

        if (!data?.ok) {
          return {
            ok: false,
            message: data?.message,
            code: data?.code,
          };
        }

        return { ok: true };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const errorFormatted = dispatch(
          LaunchError(error, false, [], {
            functionName: "startRequestAppSumoReactivationOtp",
            context: { email, lng },
            tags: { operation: "appSumoReactivationOtp" },
          })
        );
        return {
          ok: false,
          message: errorFormatted?.message,
          code: errorFormatted?.code,
        };
      }
    };
    return await run();
  };

export const startReactivateAppSumoUser =
  ({ email, password, licenseKey, otpCode }) =>
  async (dispatch) => {
    const run = async (isRetry = false) => {
      try {
        const passwordEncrypted = encryptPassword(password);
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("reactivateAppSumoUser")}/reactivateAppSumoUser`,
          data: {
            email,
            password: passwordEncrypted,
            licenseKey,
            otpCode,
          },
        });

        if (!data?.ok) {
          return {
            ok: false,
            message: data?.message,
            code: data?.code,
          };
        }

        await dispatch(startLogin(email, password));

        return { ok: true };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const responseData = error?.response?.data;
        if (responseData && typeof responseData === "object") {
          return {
            ok: false,
            message: responseData.message,
            code: responseData.code,
          };
        }
        const errorFormatted = dispatch(
          LaunchError(error, false, [], {
            functionName: "startReactivateAppSumoUser",
            context: { email },
            tags: { operation: "appSumoReactivate" },
          })
        );
        return {
          ok: false,
          message: errorFormatted?.message,
          code: errorFormatted?.code,
        };
      }
    };
    return await run();
  };

export const startCreateAdvertiserUser = (data) => async (dispatch) => {
  const run = async (isRetry = false) => {
    try {
      const token = await dispatch(renewToken());
      const passwordEncrypted = encryptPassword(data?.password);

      const response = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("createAdvertiserUser")}/createAdvertiserUser`,
        data: {
          ...data,
          password: passwordEncrypted,
          redirectUrl: `${window.location.origin}`,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return { ok: response?.data?.ok };
    } catch (error) {
      if (!isRetry && isNetworkError(error)) {
        return await run(true);
      }
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateAdvertiserUser",
          context: { email: data?.email },
          tags: { operation: "register" },
        })
      );
      return false;
    }
  };
  return await run();
};

export const login = (user) => ({
  type: types.AUTH_LOGIN,
  payload: user,
});

/**
 * Replaces the current Firebase session after Admin SDK email/password updates
 * revoke refresh tokens. Uses a custom token minted for the same uid.
 */
export const reauthenticateWithCustomToken =
  (customToken) => async (dispatch) => {
    if (!customToken) return false;

    loginInProgress = true;
    try {
      await signInWithCustomToken(auth, customToken);
      const updatedUser = auth.currentUser;
      if (!updatedUser) return false;

      await updatedUser.getIdToken(true);
      const tokenResult = await updatedUser.getIdTokenResult();
      dispatch(login(buildAuthUserPayload(updatedUser, tokenResult)));
      return true;
    } finally {
      loginInProgress = false;
    }
  };

export const setSkipLastLogin = (skip) => ({
  type: types.AUTH_SET_SKIP_LAST_LOGIN,
  payload: skip,
});

export const startSendEmailResetPassword = (email) => async (dispatch) => {
  try {
    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("sendEmailResetPassword")}/sendEmailResetPassword`,
      data: {
        email,
      },
    });
    if (data.ok) {
      return SimpleAlert({
        title: i18next.t(READY),
        text: i18next.t(RESET_PASSWORD_EMAIL_SENT),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
    }
    return data.ok;
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startSendEmailResetPassword",
        context: { email },
        tags: { operation: "reset_password" },
      })
    );
    return false;
  }
};

export const startSendEmailUserInvitation =
  ({ email, name }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "sendEmailUserInvitation"
        )}/sendEmailUserInvitation`,
        data: {
          email,
          name,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (data.ok) {
        return true;
      }
      return data.ok;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSendEmailUserInvitation",
          context: { email, name },
          tags: { operation: "user_invitation" },
        })
      );
      return false;
    }
  };

export const getPathDatabaseByRole = (user) => (_, getState) => {
  if (!user) {
    user = getState().auth.user;
  }
  const uid = user?.uid;
  const role = user?.role;
  const advertiserID = user?.advertiserID;
  const themeID = user?.themeID;
  const shopID = user?.shopID;
  const agencyID = user?.agencyID || advertiserID || shopID;

  let pathIntegrations = "";
  let pathLastSelectedIntegrationData = "";
  let pathAdsPlans = "";
  let pathOnboardingChecklist = "";
  let pathCampaignStatistics = "";
  let pathCampaignLogs = "";
  let pathCampaigns = "";
  let pathAdSets = "";
  let pathAssetsGroups = "";
  let pathAds = "";
  let pathAdsContentsUploaded = "";
  let pathBase = "";
  let pathIsPlanActive = "";
  let pathLinkedInPostShared = "";
  let pathTypeOfPlans = "";
  let pathSubscriptionPlan = "";
  let pathSubscriptionPlanInfo = "";
  let pathSubscriptionPlanInfoUsage = "";
  let pathAdvertisersByAgency = "";
  let pathAdvertisers = "";
  let pathUsers = "";
  let pathPayments = "";
  let pathLogoUrl = "";
  let pathDetailedReports = "";
  let pathBrandInformation = "";
  let pathBrandSetupVersions = "";
  let pathLocationSets = "";
  let pathListenerAdvertiserID = "";
  let pathAgencies = "";
  let isUserPlatform;
  let chatFrom = "";
  let nameIdentity = "";
  let pathNps = "";
  let pathUsageAllocation = "";
  let pathUsageConsumed = "";
  let pathHomeFeatureActivity = "";

  if (ROLES_SHOPS.includes(role)) {
    pathBase = `shops/${shopID}`;
    pathOnboardingChecklist = `onboardingChecklist/${agencyID}`;
    pathIsPlanActive = `shops/${shopID}/isPlanActiveAds`;
    pathTypeOfPlans = `shops/${shopID}/typeOfPlans`;
    pathLinkedInPostShared = `shops/${shopID}/isLinkedInPostShared`;
    pathSubscriptionPlan = `shops/${shopID}/subscriptionPlanAds`;
    pathSubscriptionPlanInfo = `shops/${shopID}/subscriptionPlanInfoAds`;
    pathSubscriptionPlanInfoUsage = `shops/${shopID}/subscriptionPlanInfoAds/usage`;
    pathAdsPlans = `shops/${shopID}/adsPlans`;
    pathPayments = `payments/${shopID}`;
    pathUsers = `shops/${shopID}/users`;
    pathIntegrations = `shops/${shopID}/integrations`;
    pathLastSelectedIntegrationData = `shops/${shopID}/lastSelectedIntegrationData`;
    pathCampaignStatistics = `adsCampaignsStatistics/${shopID}`;
    pathCampaigns = `adsCampaigns/${shopID}`;
    pathCampaignLogs = `adsCampaignsLogs/${shopID}`;
    pathAdSets = `adsGroups/${shopID}`;
    pathAssetsGroups = `assetsGroups/${shopID}`;
    pathAds = `ads/${shopID}`;
    pathAdsContentsUploaded = `adsContentsUploaded/${shopID}`;
    pathDetailedReports = `adsCampaignsDetailedReport/${shopID}`;
    pathLogoUrl = `shops/${shopID}/logoImg`;
    pathBrandInformation = `shops/${shopID}/brandInformationAds`;
    pathBrandSetupVersions = `brandSetupVersions/${shopID}`;
    pathAgencies = `agencies/${agencyID}`;
    pathAdvertisers = `advertisers`;
    pathHomeFeatureActivity = `homeFeatureActivity/${shopID}`;
    isUserPlatform = false;
    chatFrom = "shops";
    nameIdentity = "shopID";
  }
  if (ROLES_PLATFORMS.includes(role)) {
    pathBase = `advertisers/${advertiserID}`;
    pathIsPlanActive = `advertisers/${advertiserID}/isPlanActive`;
    pathOnboardingChecklist = `onboardingChecklist/${agencyID}`;
    pathTypeOfPlans = `advertisers/${advertiserID}/typeOfPlans`;
    pathLinkedInPostShared = `advertisers/${advertiserID}/isLinkedInPostShared`;
    pathSubscriptionPlan = `advertisers/${advertiserID}/subscriptionPlan`;
    pathSubscriptionPlanInfo = `advertisers/${advertiserID}/subscriptionPlanInfo`;
    pathSubscriptionPlanInfoUsage = `advertisers/${advertiserID}/subscriptionPlanInfo/usage`;
    pathAdsPlans = `advertisers/${advertiserID}/plans`;
    pathAdvertisersByAgency = `advertisersByAgency/${agencyID}`;
    pathUsers = `adsUsers/${agencyID || advertiserID}`;
    pathListenerAdvertiserID = `adsUsers/${agencyID || advertiserID}/${uid}/advertiserID`;
    pathPayments = `payments/${agencyID}`;
    pathIntegrations = `advertisers/${advertiserID}/integrations`;
    pathLastSelectedIntegrationData = `advertisers/${advertiserID}/lastSelectedIntegrationData`;
    pathCampaignLogs = `adsCampaignsLogs/${advertiserID}`;
    pathCampaignStatistics = `adsCampaignsStatistics/${advertiserID}`;
    pathCampaigns = `adsCampaigns/${advertiserID}`;
    pathAdSets = `adsGroups/${advertiserID}`;
    pathAssetsGroups = `assetsGroups/${advertiserID}`;
    pathAds = `ads/${advertiserID}`;
    pathAdsContentsUploaded = `adsContentsUploaded/${advertiserID}`;
    pathLogoUrl = `advertisers/${advertiserID}/logoUrl`;
    pathDetailedReports = `adsCampaignsDetailedReport/${advertiserID}`;
    pathBrandInformation = `advertisers/${advertiserID}`;
    pathBrandSetupVersions = `brandSetupVersions/${advertiserID}`;
    pathLocationSets = `locationSets/${advertiserID}`;
    pathAgencies = `agencies/${agencyID}`;
    isUserPlatform = true;
    chatFrom = "advertisers";
    nameIdentity = "advertiserID";
    pathNps = `nps/${agencyID}/${uid}`;
    pathUsageAllocation = `advertisers/${advertiserID}/usageAllocation`;
    pathUsageConsumed = `advertisers/${advertiserID}/usageConsumed`;
    pathHomeFeatureActivity = `homeFeatureActivity/${advertiserID}`;
  }

  return {
    pathBase,
    pathOnboardingChecklist,
    pathSubscriptionPlan,
    pathSubscriptionPlanInfo,
    pathSubscriptionPlanInfoUsage,
    pathIsPlanActive,
    pathTypeOfPlans,
    pathLinkedInPostShared,
    pathAdsPlans,
    pathPayments,
    pathUsers,
    pathIntegrations,
    pathCampaignStatistics,
    pathCampaigns,
    pathCampaignLogs,
    pathAdSets,
    pathAssetsGroups,
    pathAds,
    pathAdsContentsUploaded,
    pathAdvertisersByAgency,
    pathAdvertisers,
    pathLastSelectedIntegrationData,
    pathLogoUrl,
    pathDetailedReports,
    pathBrandInformation,
    pathBrandSetupVersions,
    pathLocationSets,
    pathListenerAdvertiserID,
    pathAgencies,
    pathNps,
    pathUsageAllocation,
    pathUsageConsumed,
    pathHomeFeatureActivity,
    isUserPlatform,
    userID: ROLES_SHOPS.includes(role) ? shopID : advertiserID,
    agencyID,
    themeID,
    role,
    chatFrom,
    nameIdentity,
  };
};

export const startLogout = () => async (dispatch) => {
  clearPrivilegedLoginFromStorage();
  off(ref(db));
  await signOut(auth);
  dispatch(resetState());
  dispatch(logout());
};

const logout = () => ({
  type: types.AUTH_LOGOUT,
});

export const checkingFinish = () => {
  return {
    type: types.AUTH_LOADING_FINISH,
  };
};

export const resetState = () => ({
  type: types.GLOBAL_RESET_STATE,
});

export const startResetPassword =
  ({ newPassword, token, autoLogin = false }) =>
  async (dispatch) => {
    try {
      const passwordEncrypted = encryptPassword(newPassword);
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("resetPassword")}/resetPassword`,
        data: {
          password: passwordEncrypted,
          token,
        },
      });

      if (autoLogin) {
        await dispatch(startLogin(data?.email, newPassword));
        return data.ok;
      }

      if (data.ok) {
        return SimpleAlert({
          title: i18next.t(READY),
          text: i18next.t(RESET_PASSWORD_SAVED),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
      }

      return data.ok;
    } catch (error) {
      console.log(error);
      const errorFormatted = getObjectError(error);
      SimpleAlert({
        title: i18next.t(ERROR),
        text: i18next.t(ERROR_DESCRIPTION_GENERIC, {
          message: errorFormatted.message,
          code: errorFormatted.code,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return false;
    }
  };

export const startListenerUserAdvertiserID =
  ({ onCallback = () => {} }) =>
  async (dispatch) => {
    try {
      const { pathListenerAdvertiserID } = dispatch(getPathDatabaseByRole());

      const dbRef = ref(db, pathListenerAdvertiserID);
      onValue(dbRef, (snapshot) => {
        const data = snapshot.val();
        onCallback(data);
      });
      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerUserAdvertiserID",
          context: {},
          tags: { operation: "listener" },
        })
      );
      return false;
    }
  };

export const verifyUserAdvertiserID = (advertiserID) => async (dispatch) => {
  try {
    await waitDelay(500);
    const { userID } = dispatch(getPathDatabaseByRole());

    if (userID === advertiserID) {
      return true;
    } else {
      await dispatch(renewToken());
      return false;
    }
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "verifyUserAdvertiserID",
        context: { advertiserID },
        tags: { operation: "verify" },
      })
    );
    return true;
  }
};

export const startLinkAnonymousAccount =
  ({ email, password }) =>
  async (dispatch, getState) => {
    const run = async (isRetry = false) => {
      try {
        const currentUser = auth.currentUser;
        const isAnonymous = getState().auth.user.isAnonymous;
        if (!isAnonymous || !currentUser) {
          return {
            ok: false,
            message: "User is not anonymous or not logged in",
            code: "NOT_ANONYMOUS_USER_OR_NOT_LOGGED_IN",
          };
        }

        const token = await dispatch(renewToken());

        // Re-authenticate user for sensitive operations
        const responseReAuthenticate = await axios({
          method: "POST",
          url: `${getCloudFunctionUrl("reAuthenticateAnonymousUser")}/reAuthenticateAnonymousUser`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const customToken = responseReAuthenticate?.data?.data?.customToken;
        const userCredentialReAuthentication = await signInWithCustomToken(
          auth,
          customToken
        );
        const updatedUserWithReAuthentication =
          userCredentialReAuthentication.user;

        // Link anonymous account to email account
        const credential = EmailAuthProvider.credential(email, password);
        const userCredential = await linkWithCredential(
          updatedUserWithReAuthentication,
          credential
        );

        try {
          await axios({
            method: "POST",
            url: `${getCloudFunctionUrl("linkAnonymousUserWithEmail")}/linkAnonymousUserWithEmail`,
            data: {
              email,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        } catch (error) {
          dispatch(
            LaunchError(error, false, [], {
              functionName: "linkAnonymousUserWithEmail",
              context: { email },
            })
          );
        }

        // After linking, get updated token and update state
        const updatedUser = userCredential.user;
        await updatedUser.getIdToken(true);
        const tokenResult = await updatedUser.getIdTokenResult();
        dispatch(login(buildAuthUserPayload(updatedUser, tokenResult)));

        const geoIP = getState().user.geoIP;
        const userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : "";
        trackingLead({
          email: updatedUser.email,
          phone: "",
          ip: geoIP?.ip,
          userAgent,
          city: geoIP?.city,
        });

        return {
          ok: true,
        };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }
        const errorFormatted = dispatch(
          LaunchError(error, false, [], {
            functionName: "startLinkAnonymousAccount",
            context: { email },
            tags: { operation: "link_anonymous_account" },
          })
        );
        return {
          ok: false,
          message: errorFormatted.message,
          code: errorFormatted.code,
        };
      }
    };
    return await run();
  };
